import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Pencil, ChevronRight, Wrench } from "lucide-react";
import { useMaintenance } from "@/components/MaintenanceLayout";
import MachineIdentity from "@/components/maintenance/MachineMark";
import { PmServiceDialog } from "@/components/maintenance/MaintenanceDialogs";
import { dateLabel } from "@/components/maintenance/lib";

// Preventive maintenance — every machine's service rhythm on one page. The
// server does ALL the date math (MAINTENANCE_PHASE2_BUILD_PLAN.md §2 Slice 2):
// this page only reads days_to_due and words it. A machine that has never been
// serviced is its own state, not an overdue one.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Th = ({ children, right, width }) => (
  <th
    className={`px-4 py-2.5 text-[9.5px] uppercase tracking-[0.09em] font-semibold text-[hsl(var(--muted-foreground))] ${right ? "text-right" : "text-left"}`}
    style={width ? { width } : undefined}
  >
    {children}
  </th>
);
const Td = ({ children, right, className = "" }) => (
  <td className={`px-4 py-[13px] border-t border-[hsl(var(--border))]/60 align-middle ${right ? "text-right" : ""} ${className}`}>{children}</td>
);

// Compact hero-typography stat — the module's number style, one size down.
function Stat({ label, value, tone, sub, last }) {
  return (
    <>
      <div className="flex flex-col gap-0.5">
        <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/70 mb-[5px]">{label}</div>
        <div className={`font-mono-data font-bold text-[1.25rem] leading-none tracking-[-0.025em] ${tone || ""}`}>{value}</div>
        {sub && <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-1.5">{sub}</div>}
      </div>
      {!last && <div className="w-px h-8 bg-[hsl(var(--border))]/70 mx-6 mt-4 shrink-0" aria-hidden="true" />}
    </>
  );
}

// Interval — value-is-target, pencil slides out on local hover (the locked
// inline-edit vocabulary; same shape as the ₹/hr RateCell).
function IntervalCell({ row, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    setValue(String(row.pm_interval_days));
    const t = setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 30);
    return () => clearTimeout(t);
  }, [editing, row.pm_interval_days]);

  const commit = async () => {
    const next = Number(String(value).trim());
    if (!Number.isInteger(next) || next < 1 || next > 3650) {
      toast.error("Enter a whole number of days between 1 and 3650.");
      return;
    }
    if (next === row.pm_interval_days) { setEditing(false); return; }
    setBusy(true);
    try {
      const r = await axios.put(`${API}/maintenance/machines/${row.machine_no}/pm-interval`, { pm_interval_days: next });
      onSave(r.data?.row);
      setEditing(false);
      toast.success(`${row.name} · service every ${next} days`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that interval.");
    } finally { setBusy(false); }
  };

  if (!canEdit) {
    return <span className="text-[12.5px] text-[hsl(var(--muted-foreground))]">every <b className="font-mono-data font-bold text-[hsl(var(--foreground))]">{row.pm_interval_days}</b> days</span>;
  }
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-[11.5px] text-[hsl(var(--muted-foreground))]">every</span>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
          }}
          onBlur={commit}
          disabled={busy}
          inputMode="numeric"
          data-testid={`mt-pm-interval-input-${row.machine_no}`}
          className="w-[70px] h-7 rounded-md border border-[hsl(var(--primary))]/60 bg-[hsl(var(--card))] px-2 text-[12.5px] font-mono-data text-right outline-none"
        />
        <span className="text-[11.5px] text-[hsl(var(--muted-foreground))]">days</span>
      </span>
    );
  }
  return (
    <span className="group/int inline-flex items-center">
      <button
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        data-testid={`mt-pm-interval-${row.machine_no}`}
        title="Change this machine's service interval"
        className="inline-flex items-center gap-1 cursor-pointer text-[12.5px] text-[hsl(var(--muted-foreground))]"
      >
        every <b className="font-mono-data font-bold text-[hsl(var(--foreground))]">{row.pm_interval_days}</b> days
        <span className="max-w-0 overflow-hidden group-hover/int:max-w-[18px] transition-[max-width] duration-200">
          <Pencil className="w-3 h-3 ml-1 text-[hsl(var(--muted-foreground))]" />
        </span>
      </button>
    </span>
  );
}

function NextDue({ row }) {
  if (row.days_to_due === null || row.days_to_due === undefined) {
    return <span className="text-[12px] text-[hsl(var(--muted-foreground))] italic">not scheduled yet</span>;
  }
  const d = row.days_to_due;
  if (d < 0) {
    return (
      <>
        <div className="font-mono-data text-[12.5px] font-extrabold rl-tx-red">OVERDUE · {Math.abs(d)} day{Math.abs(d) === 1 ? "" : "s"}</div>
        <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-[3px]">was due {dateLabel(row.next_due)}</div>
      </>
    );
  }
  return (
    <>
      <div className="text-[12.5px] font-[650]">{dateLabel(row.next_due)}</div>
      <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-[3px]">{d === 0 ? "due today" : `in ${d}d`}</div>
    </>
  );
}

// Expanded row — this machine's service history, the Timeline pattern.
function History({ machineNo, checklist }) {
  const [entries, setEntries] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/maintenance/pm/${machineNo}/entries`)
      .then((r) => { if (!cancelled) setEntries(r.data?.entries || []); })
      .catch(() => { if (!cancelled) setEntries([]); });
    return () => { cancelled = true; };
  }, [machineNo]);
  const labelOf = (k) => (checklist || []).find((c) => c.key === k)?.label || k;
  return (
    <div className="px-4 pb-4 pt-1">
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 p-3">
        {entries === null && <p className="text-[12px] text-[hsl(var(--muted-foreground))]">Loading…</p>}
        {entries && entries.length === 0 && (
          <p className="text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed">
            No services logged for this machine yet.
          </p>
        )}
        <ol className="space-y-2.5">
          {(entries || []).map((e) => (
            <li key={e.id} className="flex gap-3 text-[12px]">
              <span className="font-mono-data text-[10.5px] text-[hsl(var(--muted-foreground))] w-[86px] shrink-0 pt-[3px]">
                {e.service_date}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(e.checklist || {}).filter(([, v]) => v).map(([k]) => (
                    <span key={k} className="text-[10px] font-semibold px-2 py-[2px] rounded-full border rl-tx-green" style={{ borderColor: "rgba(21,128,61,0.35)", background: "rgba(21,128,61,0.08)" }}>
                      {labelOf(k)}
                    </span>
                  ))}
                  {!Object.values(e.checklist || {}).some(Boolean) && (
                    <span className="text-[11px] text-[hsl(var(--muted-foreground))] italic">no checklist items ticked</span>
                  )}
                </div>
                {e.parts_note && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">parts: {e.parts_note}</div>}
                {e.note && <div className="text-[11.5px] mt-1">{e.note}</div>}
                <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-1">
                  {e.id} · logged by {e.logged_by?.name || "—"}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export default function MaintenancePmPage() {
  const { can = {}, refresh, refreshKey } = useMaintenance() || {};
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [dialogFor, setDialogFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/maintenance/pm`);
      setData(r.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load preventive maintenance.");
      setData({ machines: [], checklist: [], stats: {} });
    }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const applyRow = useCallback((row) => {
    if (!row) return;
    setData((d) => (d ? { ...d, machines: d.machines.map((m) => (m.machine_no === row.machine_no ? row : m)) } : d));
  }, []);

  const logService = useCallback(async (body) => {
    if (!dialogFor) return;
    setBusy(true);
    try {
      await axios.post(`${API}/maintenance/pm/${dialogFor.machine_no}`, body);
      toast.success(`Service logged for ${dialogFor.name}`);
      setDialogFor(null);
      await load();
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log that service.");
    } finally { setBusy(false); }
  }, [dialogFor, load, refresh]);

  const rows = data?.machines || null;
  const stats = data?.stats || {};
  const serviced = rows ? rows.filter((r) => r.last_service).length : 0;

  return (
    <>
      <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] overflow-hidden" data-testid="mt-pm-page">
        <div className="px-[18px] pt-4 pb-3 border-b border-[hsl(var(--border))]">
          <div className="text-[17px] font-extrabold flex items-center gap-2.5" style={{ fontFamily: "var(--rl-font-heading), sans-serif" }}>
            <CalendarClock className="w-[18px] h-[18px] text-[#2F6DAE]" />
            Preventive Maintenance
          </div>
          <div className="text-[11.5px] text-[hsl(var(--muted-foreground))] mt-[3px]">
            Every machine's service rhythm — log a service, and the next one schedules itself
          </div>
        </div>

        <div className="px-[18px] py-3.5 flex items-start flex-wrap border-b border-[hsl(var(--border))]">
          <Stat label="Overdue" value={stats.overdue ?? "—"} tone={stats.overdue ? "rl-tx-red" : ""} sub="past the machine's interval" />
          <Stat label="Due in 30 days" value={stats.due_soon ?? "—"} sub="coming up" />
          <Stat label="Never serviced" value={stats.never ?? "—"} sub={`of ${stats.total ?? 0} machines`} last />
        </div>

        <table className="w-full border-collapse">
          <thead className="rl-thead">
            <tr>
              <Th width="32%">Machine</Th>
              <Th>Interval</Th>
              <Th>Last service</Th>
              <Th>Next due</Th>
              <Th right>Action</Th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[12px] text-[hsl(var(--muted-foreground))]">Loading…</td></tr>
            )}
            {rows && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[hsl(var(--muted-foreground))]">No machines in the registry yet.</td></tr>
            )}
            {rows && rows.length > 0 && serviced === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-5 text-center">
                  <p className="text-[13px] text-[hsl(var(--muted-foreground))] max-w-[460px] mx-auto leading-relaxed">
                    No services logged yet — log the first one and RockLink starts tracking when the next is due.
                  </p>
                </td>
              </tr>
            )}
            {(rows || []).map((r) => {
              const isExpanded = expanded === r.machine_no;
              return (
                <React.Fragment key={r.machine_no}>
                  <tr
                    className="hover:bg-[hsl(var(--muted))]/25 cursor-pointer"
                    onClick={() => setExpanded(isExpanded ? null : r.machine_no)}
                    data-testid={`mt-pm-row-${r.machine_no}`}
                  >
                    <Td>
                      <MachineIdentity machine={r} />
                    </Td>
                    <Td><IntervalCell row={r} canEdit={!!can.can_admin_maintenance} onSave={applyRow} /></Td>
                    <Td>
                      {r.last_service ? (
                        <>
                          <div className="text-[12.5px] font-[650]">{dateLabel(r.last_service)}</div>
                          <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-[3px]">
                            by {r.last_service_by || "—"}{r.entry_count > 1 ? ` · ${r.entry_count} services` : ""}
                          </div>
                        </>
                      ) : (
                        <span className="text-[12px] text-[hsl(var(--muted-foreground))] italic">never serviced</span>
                      )}
                    </Td>
                    <Td><NextDue row={r} /></Td>
                    <Td right>
                      <div className="flex items-center justify-end gap-2">
                        {can.can_repair_machines ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setDialogFor(r); }}
                            data-testid={`mt-pm-log-${r.machine_no}`}
                            className="rl-lit-btn inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full bg-[hsl(var(--primary))] text-white text-[12px] font-bold"
                          >
                            <Wrench className="w-3.5 h-3.5" /> Log service
                          </button>
                        ) : (
                          <span className="text-[11px] text-[hsl(var(--muted-foreground))] italic" title="Only the maintenance team can log a service">
                            maintenance logs services
                          </span>
                        )}
                        <ChevronRight className={`w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]/60 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                      </div>
                    </Td>
                  </tr>
                  {isExpanded && (
                    <tr><td colSpan={5} className="bg-[hsl(var(--muted))]/10"><History machineNo={r.machine_no} checklist={data?.checklist} /></td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <PmServiceDialog
        open={!!dialogFor}
        onOpenChange={(v) => !v && setDialogFor(null)}
        machine={dialogFor}
        checklist={data?.checklist}
        today={data?.today}
        busy={busy}
        onConfirm={logService}
      />
    </>
  );
}
